import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import viewIcon from "../assets/Images/View.png";
import editIcon from "../assets/Images/Edit.png";
import deleteIcon from "../assets/icons/deleteIcon.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import addIcon from "../assets/Images/AddIcon.png";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import crossIcon from "../assets/icons/crossIcon.png";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import usersIcon from "../assets/icons/users.png";
import conversion from "../assets/icons/conversation.png";
import modelsIcon from "../assets/icons/modelsvg.png";
import { useDispatch } from "react-redux";
import { setSelectedMenu } from "../redux/sidebarSlice";
import { triggerRefresh } from "../redux/refreshSlice";
import { useTableSort } from "../hooks/useTableSort";
import { SortableHeader } from "../components/SortableHeader";

const BASE_URL = `${process.env.REACT_APP_API_URL}/users`;
const ENTERPRISE_URL = `${process.env.REACT_APP_API_URL}/enterprises`;
const ROLE_URL = `${process.env.REACT_APP_API_URL}/roles`;
const DEPARTMENT_URL = `${process.env.REACT_APP_API_URL}/departments`;
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [roles, setRoles] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [userRoleName, setUserRoleName] = useState("");
  const [userCounts, setUserCounts] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  });
  const [adminUsersCount, setAdminUsersCount] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [regularUsersCount, setRegularUsersCount] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [chatStats, setChatStats] = useState([]);
  const [useDefaultPassword, setUseDefaultPassword] = useState(true);
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] =
    useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRolesAndUser = async () => {
      try {
        const res = await api.get(ROLE_URL);
        const roles = res.data;
        setRoleList(roles);

        const currentUser = JSON.parse(
          localStorage.getItem("user") || sessionStorage.getItem("user"),
        );

        const userRole = roles.find((r) => r.role_id === currentUser?.role_fid);
        if (userRole) setUserRoleName(userRole.role_name);
      } catch (err) {
        console.error("Error fetching roles or user info", err);
      }
    };

    fetchRolesAndUser();
  }, []);

  const currentUser = JSON.parse(
    localStorage.getItem("user") || sessionStorage.getItem("user"),
  );

  const preselectedEnterpriseId = location.state?.enterpriseId || null;
  const currentUserEnterpriseId = currentUser?.enterprise_fid || null;

  useEffect(() => {
    if (preselectedEnterpriseId) {
      setFormData((prev) => ({
        ...prev,
        enterprise_id: preselectedEnterpriseId,
      }));
    }
  }, [preselectedEnterpriseId]);

  const [formData, setFormData] = useState({
    profilepic: null,
    name: "",
    login_name: "",
    email: "",
    password: "",
    enterprise_id: "",
    role_id: "",
    department_fids: [],
  });

  const [previewUrl, setPreviewUrl] = useState("");

  const fetchData = async () => {
    try {
      const [roleRes, enterpriseRes, departmentRes] = await Promise.allSettled([
        api.get(ROLE_URL),
        api.get(ENTERPRISE_URL),
        api.get(DEPARTMENT_URL),
      ]);

      if (roleRes.status === "fulfilled") setRoles(roleRes.value.data);
      else console.warn("Failed to load roles:", roleRes.reason);

      if (enterpriseRes.status === "fulfilled") {
        setEnterprises(enterpriseRes.value.data);
      } else {
        console.warn("Failed to load enterprises:", enterpriseRes.reason);
      }

      if (departmentRes.status === "fulfilled") {
        setDepartments(departmentRes.value.data);
      } else {
        console.warn("Failed to load departments:", departmentRes.reason);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const fetchd = useCallback(
    async (opts = {}) => {
      try {
        const params = {
          page: opts.page ?? currentPage,
          limit: opts.limit ?? entriesPerPage,
          search: opts.search ?? searchTerm,
          dayFilter: opts.dayFilter ?? dayFilter,
          enterpriseId: opts.enterpriseId ?? preselectedEnterpriseId,
        };
        const res = await api.get(BASE_URL, {
          params,
        });
        setUsers(res.data?.data || []);
        setTotalUsers(res.data?.total || 0);
      } catch (error) {
        console.error("Failed to fetch users", error);
        showErrorToast(TOAST_MESSAGES.USER.FETCH_ERROR);
      }
    },
    [
      currentPage,
      entriesPerPage,
      searchTerm,
      dayFilter,
      preselectedEnterpriseId,
    ],
  );

  const fetchStats = async () => {
    try {
      const [userCountRes, chatSummaryRes, userAdminCountsRes] =
        await Promise.allSettled([
          api.get(`${process.env.REACT_APP_API_URL}/users/count/all`),
          api.get(`${process.env.REACT_APP_API_URL}/chat/summary`),
          api.get(`${process.env.REACT_APP_API_URL}/users/user-admin-counts`),
        ]);

      // ✅ user counts
      if (userCountRes.status === "fulfilled") {
        setUserCounts(userCountRes.value.data);
      } else {
        console.warn("Failed to load user counts:", userCountRes.reason);
      }

      // ✅ chat summary
      if (chatSummaryRes.status === "fulfilled") {
        setChatStats(chatSummaryRes.value.data);
      } else {
        console.warn("Failed to load chat summary:", chatSummaryRes.reason);
      }

      // ✅ admin & user counts
      if (userAdminCountsRes.status === "fulfilled") {
        const userAdminCountsData = userAdminCountsRes.value.data;

        setAdminUsersCount({
          total: userAdminCountsData.admins?.total ?? 0,
          active: userAdminCountsData.admins?.active ?? 0,
          inactive: userAdminCountsData.admins?.inactive ?? 0,
        });

        setRegularUsersCount({
          total: userAdminCountsData.users?.total ?? 0,
          active: userAdminCountsData.users?.active ?? 0,
          inactive: userAdminCountsData.users?.inactive ?? 0,
        });
      } else {
        console.warn(
          "Failed to load admin/user counts:",
          userAdminCountsRes.reason,
        );
      }
    } catch (err) {
      console.error("fetchStats error:", err);
    }
  };

  useEffect(() => {
    fetchStats().catch((err) => console.error("fetchStats crash:", err));
  }, []);

  useEffect(() => {
    fetchData().catch((err) => console.error("fetchData crash:", err));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dayFilter, preselectedEnterpriseId]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalUsers / entriesPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [totalUsers, entriesPerPage, currentPage]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchd().catch((err) => console.error("fetchUsers crash:", err));
    }, 250);

    return () => clearTimeout(timeout);
  }, [fetchd]);

  //VKD96 Started here for to prevent that Pass@123
  useEffect(() => {
    if (useDefaultPassword) {
      setFormData((prev) => ({
        ...prev,
        password: "Pass@123",
      }));
      setConfirmPassword("Pass@123");
    } else {
      setFormData((prev) => ({
        ...prev,
        password: "",
      }));
      setConfirmPassword("");
    }
  }, [useDefaultPassword, editingId]);

  //VKD96 Ended here for to prevent that Pass@123

  useEffect(() => {
    if (showForm && !editingId && useDefaultPassword) {
      setFormData((prev) => ({
        ...prev,
        password: "Pass@123",
      }));
      setConfirmPassword("Pass@123");
    }
  }, [showForm, editingId, useDefaultPassword]);

  useEffect(() => {
    if (showForm && !editingId && useDefaultPassword) {
      setFormData((prev) => ({
        ...prev,
        password: "Pass@123",
      }));
      setConfirmPassword("Pass@123");
    }
  }, [showForm, editingId, useDefaultPassword]);

  const enrichedUsers = useMemo(() => {
    return users.map((user) => {
      const departmentIds =
        Array.isArray(user.department_fids) && user.department_fids.length
          ? user.department_fids
          : user.department_fid
            ? [user.department_fid]
            : [];

      const departmentNames = departmentIds
        .map(
          (id) =>
            departments.find((d) => Number(d.department_id) === Number(id))
              ?.department_name,
        )
        .filter(Boolean);

      return {
        ...user,
        department_fids: departmentIds,
        role_name:
          roles.find((r) => r.role_id === user.role_fid)?.role_name || "",
        department_name:
          departmentNames.join(", ") || user.department_name || "",
      };
    });
  }, [users, roles, departments]);

  const filteredUsers = useMemo(() => enrichedUsers, [enrichedUsers]);

  const availableDepartments = useMemo(() => {
    if (!formData.enterprise_id) return departments;

    const hasEnterpriseInfo = departments.some(
      (dept) =>
        dept.enterprise_fid !== undefined && dept.enterprise_fid !== null,
    );

    if (!hasEnterpriseInfo) return departments;

    const filtered = departments.filter(
      (dept) => String(dept.enterprise_fid) === String(formData.enterprise_id),
    );

    // Fallback to all departments when API payload doesn't include matching enterprise mapping.
    return filtered.length ? filtered : departments;
  }, [departments, formData.enterprise_id]);

  const selectedDepartmentNames = useMemo(() => {
    if (!formData.department_fids?.length) return [];
    return formData.department_fids
      .map(
        (id) =>
          departments.find((dept) => String(dept.department_id) === String(id))
            ?.department_name,
      )
      .filter(Boolean);
  }, [formData.department_fids, departments]);

  const toggleDepartmentSelection = (departmentId) => {
    setFormData((prev) => {
      const idAsString = String(departmentId);
      const alreadySelected = prev.department_fids.some(
        (id) => String(id) === idAsString,
      );
      return {
        ...prev,
        department_fids: alreadySelected
          ? prev.department_fids.filter((id) => String(id) !== idAsString)
          : [...prev.department_fids, idAsString],
      };
    });
  };

  const { sortedData, requestSort, sortConfig } = useTableSort(filteredUsers);

  useEffect(() => {
    if (
      showForm &&
      !editingId &&
      !viewMode &&
      userRoleName === "Admin" &&
      currentUserEnterpriseId
    ) {
      setFormData((prev) => ({
        ...prev,
        enterprise_id: currentUserEnterpriseId,
        department_fids: [],
      }));
    }
  }, [showForm, editingId, viewMode, userRoleName, currentUserEnterpriseId]);

  const totalEntries = totalUsers;
  const totalPages = Math.max(1, Math.ceil(totalEntries / entriesPerPage));
  const showingFrom =
    totalEntries === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const showingTo = Math.min(currentPage * entriesPerPage, totalEntries);
  const currentData = sortedData;
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const resetForm = () => {
    setFormData({
      profilepic: null,
      name: "",
      login_name: "",
      email: "",
      password: "",
      enterprise_id: "",
      role_id: "",
      department_fids: [],
    });
    setPreviewUrl("");
    setEditingId(null);
    setShowForm(false);
    setConfirmPassword("");
    setViewMode(false);
    setUseDefaultPassword(true);
    setIsDepartmentDropdownOpen(false);
  };

  const handleChange = (e) => {
    let value = e.target.value.replace(/[^\p{L}0-9 .,'_-]/gu, "");

    if (value.length > 50) return; // prevent typing beyond limit

    setFormData({ ...formData, login_name: value });
  };

  const handleNameChange = (e) => {
    let value = e.target.value.replace(/[^\p{L}0-9 .,'_-]/gu, "");
    if (value.length > 50) return;
    setFormData({ ...formData, name: value });
  };

  const nameRegex = /^(?=.*\p{L})[\p{L}0-9 .,'_-]{1,50}$/u;

  const handleSubmit1 = async (e) => {
    e.preventDefault();

    const {
      name,
      login_name,
      email,
      enterprise_id,
      role_id,
      password,
      department_fids,
    } = formData;

    if (!enterprise_id) {
      showErrorToast(TOAST_MESSAGES.USER.REQUIRED_ENTERPRISE);
      return;
    }

    if (!role_id) {
      showErrorToast(TOAST_MESSAGES.USER.REQUIRED_ROLE);
      return;
    }

    if (!department_fids?.length) {
      showErrorToast("Department is required");
      return;
    }

    if (!validateEmail(email)) {
      showErrorToast(TOAST_MESSAGES.USER.INVALID_EMAIL);
      return;
    }

    if (!nameRegex.test(name.trim())) {
      showErrorToast(
        "Invalid name format. Letters, numbers & . , ' _ - only allowed.",
      );
      return;
    }

    if (!login_name.trim()) {
      showErrorToast("User name is required");
      return;
    }

    // ✅ Minimum length
    if (login_name.length < 4) {
      showErrorToast(`User name must be at least 4 characters`);
      return;
    }

    if (!name.trim()) {
      showErrorToast(TOAST_MESSAGES.USER.REQUIRED_NAME);
      return;
    }

    if (!email.trim()) {
      showErrorToast(TOAST_MESSAGES.USER.REQUIRED_EMAIL);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showErrorToast(TOAST_MESSAGES.USER.INVALID_EMAIL);
      return;
    }

    if (!editingId || password) {
      if (!password) {
        showErrorToast(TOAST_MESSAGES.USER.REQUIRED_PASSWORD);
        return;
      }
      if (password.length < 6) {
        showErrorToast(TOAST_MESSAGES.USER.PASSWORD_LENGTH);
        return;
      }
      if (!/[A-Z]/.test(password)) {
        showErrorToast(TOAST_MESSAGES.USER.PASSWORD_CAPITAL);
        return;
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        showErrorToast(TOAST_MESSAGES.USER.PASSWORD_SPECIAL);
        return;
      }
      if (password !== confirmPassword) {
        showErrorToast(TOAST_MESSAGES.USER.PASSWORD_MISMATCH);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const data = new FormData();
      data.append("user_name", name.trim());
      data.append("login_name", login_name.trim());
      data.append("user_email", email.trim());
      data.append("enterprise_fid", enterprise_id);
      data.append("role_fid", role_id);
      data.append("department_fid", department_fids[0]);
      data.append("department_fids", JSON.stringify(department_fids));

      if (password) data.append("user_password", password);
      if (formData.profilepic instanceof File)
        data.append("user_profile_pic", formData.profilepic);

      const headers = {
        "Content-Type": "multipart/form-data",
      };

      if (editingId) {
        await api.put(`${BASE_URL}/${editingId}`, data, { headers });
        showSuccessToast("User updated successfully.");
      } else {
        await api.post(BASE_URL, data, { headers });
        showSuccessToast("User added successfully.");
      }

      resetForm();
      fetchData();
      fetchd();
      fetchStats();
      dispatch(triggerRefresh());
    } catch (err) {
      console.error("Submit error:", err);

      const backendMessage =
        err?.response?.data?.message ||
        (editingId
          ? TOAST_MESSAGES.USER.UPDATE_ERROR
          : TOAST_MESSAGES.USER.CREATE_ERROR);

      showErrorToast(backendMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      profilepic: null,
      name: user.user_name,
      login_name: user.login_name,
      email: user.user_email,
      enterprise_id: user.enterprise_fid,
      role_id: user.role_fid,
      password: user.password,
      department_fids:
        (Array.isArray(user.department_fids) && user.department_fids) ||
        (user.department_fid ? [user.department_fid] : []),
    });

    setUseDefaultPassword(true);
    setPreviewUrl(
      user.user_profile_pic
        ? `${SERVER_URL}/${user.user_profile_pic.replace(/\\/g, "/")}`
        : "",
    );
    setEditingId(user.user_id);
    setViewMode(false);
    setShowForm(true);
    setIsDepartmentDropdownOpen(false);
  };

  const handleView = (user) => {
    setFormData({
      profilepic: null,
      name: user.user_name,
      login_name: user.login_name,
      email: user.user_email,
      enterprise_id: user.enterprise_fid,
      role_id: user.role_fid,
      password: "",
      department_fids:
        (Array.isArray(user.department_fids) && user.department_fids) ||
        (user.department_fid ? [user.department_fid] : []),
    });
    setPreviewUrl(
      user.user_profile_pic
        ? `${SERVER_URL}/${user.user_profile_pic.replace(/\\/g, "/")}`
        : "",
    );
    setViewMode(true);
    setShowForm(true);
    setIsDepartmentDropdownOpen(false);
  };

  const handleStatusChange = async (user) => {
    const newStatus = user.user_status === "1" ? 0 : 1;
    const statusText = newStatus === 1 ? "Active" : "Inactive";

    try {
      await api.patch(`${BASE_URL}/${user.user_id}/status`, {
        user_status: newStatus,
      });

      fetchd();
      fetchStats();
      showSuccessToast(`User status changed to ${statusText}`);
    } catch (err) {
      console.error("Status change error:", err);
      showErrorToast(TOAST_MESSAGES.USER.STATUS_CHANGE_ERROR);
    }
  };

  const exportExcel = () => {
    const wsData = [
      ["Name", "Email", "Enterprize", "Role", "Group"],
      ...filteredUsers.map((u) => [
        u.user_name,
        u.login_name,
        u.user_email,
        enterprises.find((ent) => ent.enterprise_id === u.enterprise_fid)
          ?.enterprise_name || "",
        roles.find((role) => role.role_id === u.role_fid)?.role_name || "",
        u.department_name || "",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "users.xlsx");
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const validateEmail = (email) => {
    const basicRegex = /^[^\s@]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,4}$/;
    if (!basicRegex.test(email)) return false;
    const domain = email.split("@")[1];
    const parts = domain.split(".");
    return parts.length === 2;
  };

  const overallChatSummary =
    Array.isArray(chatStats) && chatStats.length > 0
      ? chatStats[chatStats.length - 1]
      : { totalConversations: 0, usedModelCount: 0 };

  const statsData = [
    {
      label: "Total Users",
      value: regularUsersCount.total + adminUsersCount.total || 0,
      summary: [
        { label: "Users", value: regularUsersCount.total },
        { label: "Admin", value: adminUsersCount.total },
      ],
      icon: usersIcon,
    },
    {
      label: "Users",
      value: regularUsersCount.total,
      summary: [
        { label: "Active", value: regularUsersCount.active },
        { label: "Inactive", value: regularUsersCount.inactive },
      ],
      icon: usersIcon,
    },
    {
      label: "Admins",
      value: adminUsersCount.total,
      summary: [
        { label: "Active", value: adminUsersCount.active },
        { label: "Inactive", value: adminUsersCount.inactive },
      ],
      icon: usersIcon,
    },
    {
      label: "Conversations",
      value: overallChatSummary.totalConversations,
      icon: conversion,
    },
    {
      label: "Used Models",
      value: overallChatSummary.usedModelCount,
      icon: modelsIcon,
    },
  ];

  // const enrichedUsers = useMemo(() => {
  //   return users.map((user) => ({
  //     ...user,

  //     // add derived fields
  //     role_name:
  //       roles.find((r) => r.role_id === user.role_fid)?.role_name || "",

  //     department_name:
  //       departments.find((d) => d.department_id === user.department_fid)
  //         ?.department_name || "",
  //   }));
  // }, [users, roles, departments]);

  return (
    <div className="user-management-page mx-2">
      <div className="user-management-header">
        {/* Stats Boxes */}
        <div className="d-flex flex-wrap gap-2 mb-0 align-items-stretch">
          {statsData.map((item, index) => (
            <div
              key={index}
              className="dashboard-box p-3 gap-1 rounded shadow-sm d-flex flex-column justify-content-between"
            >
              <div className="d-flex align-items-center m-0">
                {item.icon && (
                  <img
                    src={item.icon}
                    alt={item.label}
                    style={{ width: "21px", height: "21px" }}
                    className="icon-image1"
                  />
                )}
                <h6 className="ms-2 mb-0">{item.label}</h6>
              </div>
              <h2 className="fw-bold m-0">
                {typeof item.value !== "undefined" ? item.value : "0"}
              </h2>
              <div className="box-summary">
                {item.summary && item.summary.length > 0 ? (
                  item.summary.map((s, i) => (
                    <span key={i}>
                      {s.label}: {s.value}
                    </span>
                  ))
                ) : (
                  <span className="invisible">placeholder</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="fw-bold refresh-prompts ak-text-lg">
            User Management Details
          </span>
        </div>

        {/* Filters & Search */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select w-auto"
              value={entriesPerPage}
              onChange={handleEntriesChange}
            >
              {[50, 100, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>

            <label className="m-0 align-self-center">Entries per page</label>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="custom-search-wrapper">
              <div className="input-group custom-search-group gap-2">
                <span className="input-group-text p-0 ps-2">
                  <i className="bi bi-search" style={{ fontSize: "16px" }}></i>
                </span>
                <input
                  type="text"
                  className="form-control p-0 px-1"
                  placeholder="Search Users"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            <button
              className="btn btn-outline-secondary action-button"
              onClick={exportExcel}
              title="Download"
            >
              <img src={downloadIcon} className="icon-image" alt="Download" />
            </button>

            <div>
              <button
                className="icon-button filter-dropdown-toggle action-button m-0"
                type="button"
                data-bs-toggle="dropdown"
                title="Filter"
              >
                <img src={filterIcon} className="icon-image" alt="Filter" />
              </button>

              <div
                className="dropdown-menu profile-menu-dropdown p-2"
                style={{ minWidth: "200px" }}
              >
                <h6 className="fw-bolder refresh-prompts">Time</h6>
                <hr />
                {["asc", "desc"].map((order) => (
                  <div className="form-check" key={order}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="sortOrder"
                      id={order}
                      value={order}
                      checked={sortOrder === order}
                      onChange={() => setSortOrder(order)}
                    />
                    <label className="form-check-label" htmlFor={order}>
                      {order === "asc" ? "Ascending" : "Descending"}
                    </label>
                  </div>
                ))}

                <hr />
                <h6 className="fw-bolder refresh-promts">By Day</h6>
                <hr />
                {["Today", "Yesterday", ""].map((day) => (
                  <div className="form-check" key={day || "all"}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="dayFilter"
                      id={day || "all"}
                      value={day}
                      checked={dayFilter === day}
                      onChange={() => setDayFilter(day)}
                    />
                    <label className="form-check-label" htmlFor={day || "all"}>
                      {day || "All"}
                    </label>
                  </div>
                ))}

                <hr />
                <div className="d-flex justify-content-between mt-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button
                    className="btn dropdown-btn bg-dark text-white btn-sm"
                    onClick={() => setCurrentPage(1)}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            </div>

            <button
              className="btn btn-outline-secondary action-button text-dark"
              onClick={() => {
                setEditingId(null);
                setViewMode(false);
                setFormData((prev) => ({
                  ...prev,
                  enterprise_id: preselectedEnterpriseId || "",
                }));
                setShowForm(true);
              }}
              title="Add user"
            >
              <img src={addIcon} className="icon-image" alt="Add" />
            </button>
          </div>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="user-management-table-panel mt-2">
          <div className="table-responsive custom-ak-scollbar">
            <table className="table table-sm w-100 m-0">
              <thead>
                <tr>
                  <th>Profile</th>
                  <SortableHeader
                    column="user_name"
                    label="Full Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="login_name"
                    label="User Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="user_email"
                    label="Email"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  {/* VKD98 started */}

                  {/* <th>Enterprise</th> */}

                  {/* VKD98 End */}

                  <SortableHeader
                    column="role_name"
                    label="Role"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="department_name"
                    label="Group"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="totalChats"
                    label="Conversations"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    className="text-center"
                  />
                  <SortableHeader
                    column="modelUsedCount"
                    label="Models Used"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    className="text-center"
                  />

                  <SortableHeader
                    column="user_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="updated_timestamp"
                    label="Last Updated"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((user, i) => (
                  <tr key={i}>
                    <td>
                      {user.user_profile_pic ? (
                        <img
                          src={`${SERVER_URL}/${user.user_profile_pic.replace(
                            /\\/g,
                            "/",
                          )}`}
                          alt="profile"
                          style={{
                            width: 25,
                            height: 25,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div className="circle-avatar">
                          {user.user_name?.charAt(0).toUpperCase() || "U"}
                        </div>
                      )}
                    </td>
                    <td
                      className="text-truncate text-left"
                      style={{ maxWidth: "150px" }}
                    >
                      {user.user_name
                        ? user.user_name.charAt(0).toUpperCase() +
                          user.user_name.slice(1)
                        : "—"}
                    </td>
                    <td
                      className="text-truncate text-left"
                      style={{ maxWidth: "120px" }}
                    >
                      {typeof user?.login_name === "string" &&
                      user.login_name.trim()
                        ? user.login_name.charAt(0).toUpperCase() +
                          user.login_name.slice(1)
                        : "–"}
                    </td>

                    <td
                      className="text-truncate text-left"
                      style={{ maxWidth: "150px" }}
                    >
                      {user.user_email}
                    </td>
                    {/* VKD98 started */}

                    {/* <td>
                      {enterprises.find(
                        (ent) => ent.enterprise_id === user.enterprise_fid,
                      )?.enterprise_name || (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td> */}
                    {/* VKD98 End */}

                    <td>
                      {roles.find((role) => role.role_id === user.role_fid)
                        ?.role_name || (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td>
                    <td style={{ width: "120px" }}>
                      {user.department_name || (
                        <span className="text-muted">Unknown</span>
                      )}
                    </td>
                    <td className="text-center" style={{ width: "75px" }}>
                      {user.totalChats > 0 ? (
                        <span
                          className="text-primary text-decoration-none"
                          role="button"
                          onClick={() => {
                            dispatch(
                              setSelectedMenu("Conversation Managements"),
                            );
                            navigate("/admin/user-session-management", {
                              state: {
                                user_id: user.user_id,
                                user_name: user.user_name,
                              },
                            });
                          }}
                        >
                          {user.totalChats}
                        </span>
                      ) : (
                        "0"
                      )}
                    </td>

                    <td className="text-center">
                      {user.modelUsedCount > 0 ? (
                        <span
                          className="text-primary text-decoration-none"
                          role="button"
                          onClick={() => {
                            dispatch(
                              setSelectedMenu("Conversation Managements"),
                            );
                            navigate("/admin/user-session-management", {
                              state: {
                                user_id: user.user_id,
                                user_name: user.user_name,
                              },
                            });
                          }}
                        >
                          {user.modelUsedCount}
                        </span>
                      ) : (
                        "0"
                      )}
                    </td>

                    <td style={{ width: "75px" }}>
                      {user.user_status === "1" ? (
                        <span>Active</span>
                      ) : (
                        <span>Inactive</span>
                      )}
                    </td>
                    <td style={{ width: "120px" }}>
                      {formatDateToUserTimeZone(user.updated_timestamp)}
                    </td>
                    <td style={{ width: "75px" }}>
                      <button
                        className="icon-button"
                        onClick={() => handleView(user)}
                        title="View"
                      >
                        <img src={viewIcon} className="icon-image" alt="View" />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleEdit(user)}
                        title="Edit"
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleStatusChange(user)}
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0 gap-2">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-75 mx-auto">
              <form onSubmit={handleSubmit1}>
                <div className="modal-header justify-content-center">
                  <h5 className="modal-title">
                    {viewMode
                      ? "View User Details"
                      : editingId
                        ? "Update User Details"
                        : "Add User Details"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={resetForm}
                    aria-label="Close"
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">
                        Enterprise
                      </label>
                      <select
                        value={formData.enterprise_id}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            enterprise_id: e.target.value,
                            department_fids: [],
                          });
                          setIsDepartmentDropdownOpen(false);
                        }}
                        className="form-select"
                        disabled={userRoleName !== "Superadmin" || viewMode}
                      >
                        <option value="">Select Enterprize</option>
                        {enterprises.map((enterprise) => (
                          <option
                            key={enterprise.enterprise_id}
                            value={enterprise.enterprise_id}
                          >
                            {enterprise.enterprise_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">Role</label>
                      <select
                        className="form-select"
                        value={formData.role_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            role_id: e.target.value,
                          })
                        }
                        required
                        disabled={viewMode}
                      >
                        <option value="">Select Role</option>
                        {/* {roles
                          .filter((role) => role.role_status === "1")
                          .filter((role) =>
                            userRoleName?.toLowerCase() === "superadmin" &&
                            !preselectedEnterpriseId
                              ? true
                              : ["admin", "user"].includes(
                                  role.role_name.toLowerCase(),
                                ),
                          )
                          .sort((a, b) =>
                            a.role_name.localeCompare(b.role_name),
                          )
                          .map((role) => (
                            <option key={role.role_id} value={role.role_id}>
                              {role.role_name}
                            </option>
                          ))} */}

                        {roles
                          .filter((role) => role.role_status === "1")
                          .filter((role) =>
                            userRoleName?.toLowerCase() === "superadmin" &&
                            !preselectedEnterpriseId
                              ? true
                              : ["admin", "user"].includes(
                                  role.role_name?.toLowerCase(),
                                ),
                          )
                          .sort((a, b) => {
                            const nameA = a.role_name ?? "";
                            const nameB = b.role_name ?? "";
                            return nameA.localeCompare(nameB);
                          })
                          .map((role) => (
                            <option key={role.role_id} value={role.role_id}>
                              {role.role_name || "Unnamed Role"}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">Group</label>
                      <div className="position-relative">
                        <button
                          type="button"
                          className="form-select text-start"
                          disabled={viewMode}
                          onClick={() =>
                            setIsDepartmentDropdownOpen((prev) => !prev)
                          }
                        >
                          {selectedDepartmentNames.length
                            ? selectedDepartmentNames.join(", ")
                            : "Select Group"}
                        </button>

                        {isDepartmentDropdownOpen && (
                          <div
                            className="position-absolute w-100 border rounded p-2 mt-1"
                            style={{
                              maxHeight: "180px",
                              overflowY: "auto",
                              zIndex: 1060,
                              background: "var(--btn-color)",
                            }}
                          >
                            {availableDepartments.length === 0 ? (
                              <div className="text-muted small px-1">
                                No groups available
                              </div>
                            ) : (
                              availableDepartments.map((dept) => (
                                <div
                                  className="form-check"
                                  key={dept.department_id}
                                >
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`admin-dept-${dept.department_id}`}
                                    checked={formData.department_fids.some(
                                      (id) =>
                                        String(id) ===
                                        String(dept.department_id),
                                    )}
                                    onChange={() =>
                                      toggleDepartmentSelection(
                                        dept.department_id,
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`admin-dept-${dept.department_id}`}
                                  >
                                    {dept.department_name}
                                  </label>
                                </div>
                              ))
                            )}
                          </div>
                        )}

                        <input
                          type="text"
                          className="d-none"
                          required
                          value={formData.department_fids.join(",")}
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">
                        User Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.login_name}
                        onChange={handleChange}
                        required
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">
                        Full Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={handleNameChange}
                        required
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label required-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name={`email-${editingId || "new"}`}
                        value={formData.email}
                        onChange={(e) => {
                          const email = e.target.value;
                          if (validateEmail(email) || email === "") {
                            setFormData({ ...formData, email });
                          } else {
                            setFormData({ ...formData, email });
                            console.warn("Invalid email format");
                          }
                        }}
                        autoComplete="new-email"
                        required
                        disabled={viewMode}
                      />
                      {formData.email && !validateEmail(formData.email) && (
                        <small className="text-danger">
                          Invalid email format
                        </small>
                      )}
                    </div>

                    {!viewMode && (
                      <div className="mt-4 col-md-4">
                        <input
                          type="checkbox"
                          id="defaultPassword"
                          checked={useDefaultPassword}
                          onChange={(e) =>
                            setUseDefaultPassword(e.target.checked)
                          }
                        />
                        <label htmlFor="defaultPassword" className="ms-2">
                          Generate Temporary Password (Pass@123)
                        </label>
                      </div>
                    )}

                    {!viewMode && (
                      <>
                        <div className="col-md-4 mb-3">
                          <label className="form-label required-label">
                            Password
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            value={formData.password}
                            disabled={useDefaultPassword}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                password: e.target.value,
                              })
                            }
                            required
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label required-label">
                            Confirm Password
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            value={confirmPassword}
                            disabled={useDefaultPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Profile Picture</label>
                          <input
                            type="file"
                            className="file-input-reverse"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              setFormData({
                                ...formData,
                                profilepic: file,
                              });
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setPreviewUrl(reader.result);
                                };
                                reader.readAsDataURL(file);
                              } else {
                                setPreviewUrl("");
                              }
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancle-btn"
                    onClick={resetForm}
                  >
                    {viewMode ? "Close" : "Cancel"}
                  </button>
                  {!viewMode && (
                    <button
                      type="submit"
                      className="custom-btn"
                      disabled={isSubmitting}
                    >
                      {editingId ? "Update" : "Submit"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
